import { UserAvatar } from "@/js/Components/UserAvatar";
import { useConnectorLink } from "@/js/Layouts/Connector";
import { SocialAccountType } from "@/js/types";
import CircleCheckIcon from "@/svg/circle-check-solid.svg?react";
import FacebookIcon from "@/svg/facebook-logo.svg?react";
import LinkedInIcon from "@/svg/linkedin-logo.svg?react";
import TikTokIcon from "@/svg/tiktok-logo.svg?react";
import YouTubeIcon from "@/svg/youtube-logo.svg?react";
import { Clickable } from "@enymo/react-clickable-router";
import classNames from "classnames";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import route from "ziggy-js";

const icons: Record<Exclude<SocialAccountType, "instagram">, React.FC<React.SVGProps<SVGSVGElement>>> = {
    facebook: FacebookIcon,
    youtube: YouTubeIcon,
    linkedin: LinkedInIcon,
    tiktok: TikTokIcon
}

function SocialAccountLink({
    type,
    connected,
    to,
    firstName,
    lastName
}: {
    type: Exclude<SocialAccountType, "instagram">,
    connected: boolean,
    to: string,
    firstName: string,
    lastName: string
}) {
    const {t} = useTranslation();
    const Icon = icons[type];

    return <>
        <Clickable to={to} disabled={connected} linkType="no-router" className={classNames("rounded-lg border-2 border-neutral-200 h-17 px-5 flex items-center gap-5", connected ? "bg-bg-400" : "bg-bg-100")}>
            <Icon className="size-10" />
            <div className="flex-1 flex flex-col gap-0.5">
                <div className="body-l-md">{t(`socialAccount.${type}.title`)}</div>
                {connected ? (
                    <div className="flex items-center gap-1.5 font-medium text-sm text-success">
                        <CircleCheckIcon className="fill-success size-4" />
                        {t("connector.connected")}
                    </div>
                ) : (
                    <div className="body-s text-text-600">{t(`socialAccount.${type}.text`)}</div>
                )}
            </div>
        </Clickable>
        {type === "facebook" && (
            <p className="body-xs">
                <Trans i18nKey="socialAccount.facebook.disclaimer.text">
                    If you do not yet have a Meta Business Portfolio, you can find instructions for creating one <a className="text-primary-500" href="https://www.youtube.com/watch?v=bmhFVpDmlWY" target="_blank" referrerPolicy="no-referrer" title={t("socialAccount.facebook.disclaimer.href")}>here</a>
                    Alternatively, you may contact {{firstName}} {{lastName}} for alternative connection options.
                </Trans>
            </p>
        )}
    </>
}

export default function Main() {
    const {t} = useTranslation();

    const {connectorLink: {sid, user, accounts}} = useConnectorLink();

    return (
        <div className="flex flex-col px-6 py-10 rounded-xl bg-bg-100 shadow-card max-w-[500px] w-full">
            <UserAvatar className="self-center" sid={sid} user={user} size={80} />
            <h1 className="heading-m mt-7.5 text-center">{t("connector.connect.title")}</h1>
            <h2 className="heading-l mt-4 text-center">{user.first_name} {user.last_name}</h2>
            <p className="body-m mt-8 text-center">{t("connector.connect.text")}</p>
            <div className="flex flex-col mt-6 gap-3">
                {accounts.map(({type, connected}) => (
                    <SocialAccountLink firstName={user.first_name} lastName={user.last_name} type={type} connected={connected} to={import.meta.env.VITE_APP_URL + route(`oauth.${type === "youtube" ? "google" : type}`, {
                        sid,
                        action: "connect",
                        type: type === "facebook" ? "system" : undefined
                    }, false)} />
                ))}
            </div>
        </div>
    )
}